//Emails.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Avatar,
  Typography,
} from '@mui/material';
import { acme, acmePageSectionCardSx, acmeBillingSectionContentSx } from '../../theme/acmeTheme';
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card,
  CardContent,
  Table,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  IconButton,
  MenuItem,
  TableActionsMenu,
  TableActionsMenuButton,
  destructiveMenuItemSx,
  stopTableRowClick,
} from "../ui";
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { CheckCircle, Error } from '@mui/icons-material';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { 
  SpamScoreIndicator, 
  ReputationIndicator, 
  EngagementIndicator 
} from '../RatingIndicators';
import DomainPasswordDialog from '../DomainPasswordDialog';

const Emails = () => {
  const [emails, setEmails] = useState([]);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [openVerify, setOpenVerify] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();

  // === ????? ????????? ??? ?????? ? ???????? ===
  const [openAvatarDialog, setOpenAvatarDialog] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [refreshingRatings, setRefreshingRatings] = useState({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [checkingEmails, setCheckingEmails] = useState({});
  const [emailActionMenu, setEmailActionMenu] = useState({ anchorEl: null, email: null });
  
  // Состояния для диалога пароля домена
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  // =============================================

  // Function to fetch the list of emails
  const fetchEmails = async () => {
    try {
      const response = await axios.get('/api/emails');
      setEmails(response.data);
    } catch (error) {
      console.error('Failed to fetch email list', error);
      setErrorMessage('Failed to fetch email list');
    }
  };

  useEffect(() => {
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load
  }, []);

  // Function to send verification email
  const sendVerificationEmail = async () => {
    if (!email) {
      setErrorMessage('Please enter an email.');
      return;
    }

    try {
      await axios.post('/api/emails/send-verification-code', { 
        email, 
        emailPassword: emailPassword 
      });
      setSuccessMessage('Verification email sent. Please check your inbox.');
      setEmailPassword('');
      setOpenVerify(true);
    } catch (error) {
      console.error('Failed to send verification email', error);
      
      // Проверяем, является ли это защищенным email'ом
      if (error.response?.data?.isProtectedEmail) {
        setPendingEmail(email);
        setPasswordDialogOpen(true);
        return;
      }
      
      // Проверяем, заблокирован ли пользователь
      if (error.response?.status === 403 && error.response?.data?.error === 'Domain access blocked') {
        setErrorMessage('Your account has been blocked from adding emails due to multiple unauthorized attempts.');
        return;
      }
      
      if (error.response?.data?.error) {
        setErrorMessage(`Error: ${error.response.data.error}`);
      } else {
        setErrorMessage('Failed to send verification email');
      }
    }
  };

  // Обработчик успешной проверки пароля email'а
  const handleEmailPasswordSuccess = (password) => {
    setEmailPassword(password);
    setPasswordDialogOpen(false);
    
    // Повторно пытаемся отправить код с паролем
    setTimeout(() => {
      sendVerificationEmailWithPassword(pendingEmail, password);
    }, 100);
  };

  // Функция для отправки кода с паролем
  const sendVerificationEmailWithPassword = async (emailToAdd, password) => {
    try {
      await axios.post('/api/emails/send-verification-code', { 
        email: emailToAdd, 
        emailPassword: password 
      });
      setSuccessMessage('Verification email sent for protected domain. Please check your inbox.');
      setEmail(emailToAdd);
      setEmailPassword('');
      setPendingEmail('');
      setOpenVerify(true);
    } catch (error) {
      console.error('Failed to send verification email with password', error);
      setErrorMessage(error.response?.data?.error || 'Failed to send verification email');
      
      // Если пользователь заблокирован
      if (error.response?.status === 403 && error.response?.data?.error === 'Domain access blocked') {
        setErrorMessage('Your account has been blocked from adding emails due to multiple unauthorized attempts.');
      }
    }
  };

  // Обработчик закрытия диалога пароля
  const handleEmailPasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPendingEmail('');
    setEmailPassword('');
  };

  // Function to verify email
  const verifyEmail = async () => {
    try {
      await axios.post('/api/emails/verify', { email, verificationCode });
      fetchEmails();
      setOpenVerify(false);
      setEmail('');
      setVerificationCode('');
      setSuccessMessage('Email successfully verified!');
    } catch (error) {
      console.error('Failed to verify email', error);
      setErrorMessage('Verification failed. Please check the code and try again.');
    }
  };

  // Function to send verification code for the selected email
  const handleSendCode = async (email) => {
    try {
      await axios.post('/api/emails/send-verification-code', { email });
      setSuccessMessage('Verification code sent. Please check your inbox.');
      setEmail(email);
      setOpenVerify(true);
    } catch (error) {
      console.error('Failed to send verification code', error);
      if (error.response?.data?.error) {
        setErrorMessage(`Error: ${error.response.data.error}`);
      } else {
        setErrorMessage('Failed to send verification code');
      }
    }
  };

  // Function to open the delete confirmation dialog
  const handleOpenDeleteDialog = (email) => {
    setSelectedEmail(email);
    setOpenDelete(true);
  };

  // Function to close the delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDelete(false);
    setSelectedEmail(null);
  };

  // Function to delete an email
  const handleDeleteEmail = async () => {
    try {
      await axios.delete(`/api/emails/${selectedEmail._id}`);
      setEmails(emails.filter((e) => e._id !== selectedEmail._id));
      setOpenDelete(false);
      setSelectedEmail(null);
      setSuccessMessage('Email successfully deleted.');
    } catch (error) {
      console.error('Failed to delete email:', error);
      setErrorMessage('Failed to delete email');
    }
  };

  // ============ ?????? ? ???????? ============
  // ????????? ?????? ???????? ??????? (???????? email-??????)
  const handleOpenAvatarDialog = (emailObj) => {
    setSelectedEmail(emailObj);
    setAvatarFile(null); // ?????????? ?????????? ?????
    setOpenAvatarDialog(true);
  };

  const handleCloseAvatarDialog = () => {
    setOpenAvatarDialog(false);
    setSelectedEmail(null);
    setAvatarFile(null);
  };

  // ????? ???????????? ???????? ????
  const handleAvatarFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate SVG format
      if (file.type !== 'image/svg+xml' && !file.name.toLowerCase().endsWith('.svg')) {
        setErrorMessage('Please select an SVG file. BIMI requires SVG format for optimal compatibility.');
        return;
      }
      
      // Validate file size (100KB limit updated)
      if (file.size > 100 * 1024) {
        setErrorMessage('SVG file is too large. Please use an SVG file under 100KB.');
        return;
      }
      
      setAvatarFile(file);
    }
  };

  // ?????????? (????????) ????? ?? ??????
  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      setErrorMessage('Please select an image file.');
      return;
    }
    if (!selectedEmail) {
      setErrorMessage('No email is selected.');
      return;
    }

    try {
      setAvatarUploading(true);
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      await axios.put(`/api/emails/${selectedEmail._id}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccessMessage('Avatar uploaded successfully!');
      fetchEmails();
      window.dispatchEvent(new CustomEvent("domainsUpdated"));

      setAvatarUploading(false);
      handleCloseAvatarDialog();
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      setErrorMessage('Failed to upload avatar');
      setAvatarUploading(false);
    }
  };
  // ==========================================

  // Функция для обновления рейтингов конкретного email'а
  const handleRefreshEmailRatings = async (emailId) => {
    setRefreshingRatings(prev => ({ ...prev, [emailId]: true }));
    try {
      await axios.put(`/api/emails/${emailId}/ratings`);
      await fetchEmails(); // Обновляем список email'ов
      setSuccessMessage('Ratings updated successfully');
    } catch (error) {
      console.error('Error updating email ratings:', error);
      setErrorMessage('Error updating ratings');
    } finally {
      setRefreshingRatings(prev => ({ ...prev, [emailId]: false }));
    }
  };

  // Функция для обновления всех рейтингов
  const handleRefreshAllRatings = async () => {
    setRefreshingAll(true);
    try {
      await axios.post('/api/emails/update-all-ratings');
      await fetchEmails(); // Обновляем список email'ов
      setSuccessMessage('All ratings updated successfully');
    } catch (error) {
      console.error('Error updating all ratings:', error);
      setErrorMessage('Error updating all ratings');
    } finally {
      setRefreshingAll(false);
    }
  };

  // Функция для проверки BIMI записи для email
  const handleCheckEmailBimi = async (emailId) => {
    setCheckingEmails(prev => ({ ...prev, [emailId]: true }));
    try {
      const response = await axios.post(`/api/emails/${emailId}/check-bimi`);
      
      // Обновляем состояние emails с результатами проверки BIMI
      setEmails(emails.map(e => {
        if (e._id === emailId) {
          return {
            ...e,
            bimiRecord: response.data.bimiRecord,
            bimiStatus: response.data.bimiStatus
          };
        }
        return e;
      }));
      
      // Проверяем есть ли предупреждение
      if (response.data.warning) {
        setErrorMessage(response.data.warning);
      } else if (response.data.bimiStatus) {
        setSuccessMessage('BIMI record is valid for this email');
      } else {
        setErrorMessage('BIMI record not found or invalid for this email');
      }
    } catch (error) {
      console.error('Error checking email BIMI:', error);
      setErrorMessage('Failed to check BIMI record');
    } finally {
      setCheckingEmails(prev => ({ ...prev, [emailId]: false }));
    }
  };

  // Функция для копирования в буфер обмена
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccessMessage('BIMI record copied to clipboard!');
    }, (err) => {
      console.error('Failed to copy: ', err);
      setErrorMessage('Failed to copy BIMI record.');
    });
  };

  const openEmailActionMenu = (event, emailRow) => {
    event.stopPropagation();
    setEmailActionMenu({ anchorEl: event.currentTarget, email: emailRow });
  };

  const closeEmailActionMenu = () => {
    setEmailActionMenu({ anchorEl: null, email: null });
  };

  const handleEmailActionMenuItem = (action) => {
    const emailRow = emailActionMenu.email;
    if (!emailRow) return;
    closeEmailActionMenu();

    if (action === 'copyBimi') {
      if (emailRow.bimiRecord) {
        copyToClipboard(emailRow.bimiRecord);
      }
      return;
    }
    if (action === 'checkEmail') {
      handleCheckEmailBimi(emailRow._id);
      return;
    }
    if (action === 'sendCode') {
      handleSendCode(emailRow.email);
      return;
    }
    if (action === 'delete') {
      handleOpenDeleteDialog(emailRow);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Verified Emails"
        description="Add and verify sender addresses for your campaigns."
        sx={{ mb: 2 }}
        action={
          <Tooltip title="Update all ratings">
            <IconButton
              onClick={handleRefreshAllRatings}
              disabled={refreshingAll}
              aria-label="Update all ratings"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      <PageSnackbars {...snackbarProps} />

      <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none', mt: 0 }}>
        <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Verified Emails</PageBlockTitle>

      <Box sx={{ mb: 2 }}>
          <Input
            label="example@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="default"
            startIcon={<AddIcon />}
            onClick={sendVerificationEmail}
            fullWidth
          >
            Add Working Email
          </Button>
      </Box>

      <Table containerSx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              {/* ????????? ????? ??????? Avatar */}
              <TableCell>Avatar</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Spam Score</TableCell>
              <TableCell>Reputation</TableCell>
              <TableCell>Engagement</TableCell>
              <TableCell>BIMI Status</TableCell>
              <TableCell>Verified</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.length > 0 ? (
              emails.map((e) => (
                <TableRow key={e._id}>
                  {/* ?????? ? ???????? */}
                  <TableCell>
                    {e.avatarUrl ? (
                      <Tooltip title="Change avatar">
                        <IconButton onClick={() => handleOpenAvatarDialog(e)}>
                          {/* ?????????? ?????? ? ?????? */}
                          <Avatar src={e.avatarUrl} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Upload avatar">
                        <IconButton onClick={() => handleOpenAvatarDialog(e)}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell>{e.email}</TableCell>
                  <TableCell>
                    <SpamScoreIndicator 
                      score={e.spamScore || 0}
                      onRefresh={() => handleRefreshEmailRatings(e._id)}
                      loading={refreshingRatings[e._id]}
                    />
                  </TableCell>
                  <TableCell>
                    <ReputationIndicator 
                      reputation={e.reputation || 0}
                      onRefresh={() => handleRefreshEmailRatings(e._id)}
                      loading={refreshingRatings[e._id]}
                    />
                  </TableCell>
                  <TableCell>
                    <EngagementIndicator 
                      engagementRate={e.engagementRate || 0}
                      onRefresh={() => handleRefreshEmailRatings(e._id)}
                      loading={refreshingRatings[e._id]}
                    />
                  </TableCell>
                  <TableCell>
                    {e.bimiStatus 
                      ? <CheckCircle sx={{ color: acme.colors.success }} /> 
                      : <Error sx={{ color: acme.colors.destructive }} />
                    }
                  </TableCell>
                  <TableCell>{e.isVerified ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right" onClick={stopTableRowClick}>
                    <TableActionsMenuButton
                      ariaLabel="Email actions"
                      onClick={(event) => openEmailActionMenu(event, e)}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} style={{ border: 'none', padding: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 8,
                      px: 4,
                      textAlign: 'center'
                    }}
                  >
                    <MailOutlineIcon sx={{ fontSize: 80, color: acme.colors.mutedForegroundSolid, mb: 3, opacity: 0.5 }} />
                    <Typography
                      variant="h5"
                      sx={{
                        color: acme.colors.foreground,
                        fontWeight: 500,
                        mb: 2
                      }}
                    >
                      No working emails added yet
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: acme.colors.mutedForegroundSolid,
                        maxWidth: 500,
                        lineHeight: 1.6,
                        mb: 1
                      }}
                    >
                      Add and verify your email addresses to start sending campaigns. 
                      Each email will be analyzed for spam score, reputation, and engagement metrics to optimize your deliverability.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: acme.colors.mutedForegroundSolid,
                        fontStyle: 'italic'
                      }}
                    >
                      Enter an email address above and click "Send Verification Email" to get started.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TableActionsMenu
          anchorEl={emailActionMenu.anchorEl}
          open={Boolean(emailActionMenu.anchorEl)}
          onClose={closeEmailActionMenu}
        >
          <MenuItem
            disabled={!emailActionMenu.email?.bimiRecord}
            onClick={() => handleEmailActionMenuItem('copyBimi')}
          >
            Copy BIMI
          </MenuItem>
          <MenuItem
            disabled={checkingEmails[emailActionMenu.email?._id]}
            onClick={() => handleEmailActionMenuItem('checkEmail')}
          >
            {checkingEmails[emailActionMenu.email?._id] ? 'Checking...' : 'Check Email'}
          </MenuItem>
          {!emailActionMenu.email?.isVerified && (
            <MenuItem onClick={() => handleEmailActionMenuItem('sendCode')}>
              Send Code
            </MenuItem>
          )}
          <MenuItem
            onClick={() => handleEmailActionMenuItem('delete')}
            sx={destructiveMenuItemSx}
          >
            Delete
          </MenuItem>
        </TableActionsMenu>

        </CardContent>
      </Card>

      {/* Dialog for delete confirmation */}
      <Dialog open={openDelete} onClose={handleCloseDeleteDialog}>
        <DialogTitleText>Delete Confirmation</DialogTitleText>
        <DialogBody>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
            Are you sure you want to delete the email <strong>{selectedEmail?.email}</strong>?
          </Typography>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseDeleteDialog}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleDeleteEmail} sx={{ bgcolor: acme.colors.destructive, '&:hover': { bgcolor: acme.colors.destructive, opacity: 0.9 } }}>
            Delete
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Dialog for email verification */}
      <Dialog
        open={openVerify}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setOpenVerify(false);
          }
        }}
      >
        <DialogTitleText>Email Verification</DialogTitleText>
        <DialogBody>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}>
            We have sent a verification code to {email}. Please enter the code below.
          </Typography>
          <Input
            autoFocus
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpenVerify(false)}>
            Cancel
          </Button>
          <Button variant="default" onClick={verifyEmail}>
            Verify
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Dialog for Avatar upload */}
      <Dialog open={openAvatarDialog} onClose={handleCloseAvatarDialog}>
        <DialogTitleText>Upload or Change Avatar</DialogTitleText>
        <DialogBody>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            BIMI (Brand Indicators for Message Identification) Requirements:
          </Typography>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}>
            • Only <strong>SVG format</strong> is supported for optimal BIMI compliance<br/>
            • SVG files should be under 100KB in size<br/>
            • Logo should be square (equal width and height)<br/>
            • <strong>⚠️ Gmail requires VMC/CMC certificate + registered trademark for logo display</strong><br/>
            • This avatar will appear next to your emails in some email clients (not Gmail without VMC)
          </Typography>
          <input
            type="file"
            accept=".svg,image/svg+xml"
            onChange={handleAvatarFileChange}
          />
          {avatarFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected file: {avatarFile.name}
            </Typography>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseAvatarDialog}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleAvatarUpload} disabled={avatarUploading}>
            {avatarUploading ? 'Uploading...' : 'Save'}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Диалог для ввода пароля защищенного email домена */}
      <DomainPasswordDialog
        open={passwordDialogOpen}
        onClose={handleEmailPasswordDialogClose}
        onSuccess={handleEmailPasswordSuccess}
        email={pendingEmail}
        action="add email"
      />
    </PageShell>
  );
};

export default Emails;
